'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    activateAutoPayment,
    AutoPaymentStatus,
    cancelAutoPayment,
    getAutoPaymentStatus,
    resumeAutoPayment,
} from '@/lib/api/auto-payment/auto-payment-api'
import { useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdAutorenew,
    MdCheckCircle,
    MdCreditCard,
    MdWarning,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const onlyDigits = (v: string) => v.replace(/\D/g, '')

const formatCardNumber = (value: string) =>
    onlyDigits(value)
        .slice(0, 16)
        .replace(/(\d{4})(?=\d)/g, '$1 ')

const formatCpfCnpj = (value: string) => {
    const d = onlyDigits(value)
    if (d.length <= 11) {
        return d
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
    }
    return d
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2')
}

const formatPostalCode = (value: string) =>
    onlyDigits(value).slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2')

const formatCurrency = (cents: number | null | undefined) =>
    ((cents || 0) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

const formatDate = (s: string | null) =>
    s ? new Date(s).toLocaleDateString('pt-BR') : '-'

export default function AutoPaymentCard() {
    const { user } = useAuth()
    const [loading, setLoading] = useState(true)
    const [status, setStatus] = useState<AutoPaymentStatus | null>(null)
    const [showForm, setShowForm] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [showCancel, setShowCancel] = useState(false)
    const [busy, setBusy] = useState(false)

    // Form do cartão
    const [cardHolder, setCardHolder] = useState('')
    const [cardNumber, setCardNumber] = useState('')
    const [expiryMonth, setExpiryMonth] = useState('')
    const [expiryYear, setExpiryYear] = useState('')
    const [ccv, setCcv] = useState('')
    const [holderName, setHolderName] = useState('')
    const [holderEmail, setHolderEmail] = useState('')
    const [holderCpfCnpj, setHolderCpfCnpj] = useState('')
    const [holderPostalCode, setHolderPostalCode] = useState('')
    const [holderAddressNumber, setHolderAddressNumber] = useState('')
    const [consent, setConsent] = useState(false)

    const load = async () => {
        setLoading(true)
        const r = await getAutoPaymentStatus()
        if (r.success) setStatus(r.result)
        setLoading(false)
    }

    useEffect(() => {
        load()
    }, [])

    const openForm = () => {
        const company = (user as any)?.company
        setHolderName(company?.name || (user as any)?.name || '')
        setHolderEmail(company?.email || (user as any)?.email || '')
        setHolderCpfCnpj(company?.cnpj || company?.cpf || '')
        setShowForm(true)
    }

    const handleActivate = async () => {
        if (!consent) {
            toast.warning('É necessário autorizar a cobrança automática.')
            return
        }
        setSubmitting(true)
        try {
            const r = await activateAutoPayment({
                creditCard: {
                    holderName: cardHolder,
                    number: onlyDigits(cardNumber),
                    expiryMonth,
                    expiryYear,
                    ccv,
                },
                creditCardHolderInfo: {
                    name: holderName || cardHolder,
                    email: holderEmail,
                    cpfCnpj: onlyDigits(holderCpfCnpj),
                    postalCode: onlyDigits(holderPostalCode),
                    addressNumber: holderAddressNumber,
                },
                consent,
            })
            if (r.success) {
                toast.success(
                    r.result?.charged === false
                        ? 'Pagamento automático ativado! A mensalidade deste ciclo já está paga — a primeira cobrança no cartão sai no próximo vencimento.'
                        : 'Pagamento automático ativado com sucesso!',
                )
                setShowForm(false)
                setConsent(false)
                await load()
            } else {
                toast.error(r.error || 'Não foi possível ativar o pagamento automático.')
            }
        } catch {
            toast.error('Erro ao ativar pagamento automático.')
        } finally {
            setSubmitting(false)
        }
    }

    const handleCancel = async () => {
        if (!status?.recurring) return
        setBusy(true)
        try {
            const r = await cancelAutoPayment(status.recurring.id)
            if (r.success) {
                toast.success('Pagamento automático cancelado.')
                setShowCancel(false)
                await load()
            } else {
                toast.error(r.error || 'Erro ao cancelar.')
            }
        } finally {
            setBusy(false)
        }
    }

    const handleResume = async () => {
        if (!status?.recurring) return
        setBusy(true)
        try {
            const r = await resumeAutoPayment(status.recurring.id)
            if (r.success) {
                toast.success('Pagamento automático retomado.')
                await load()
            } else {
                toast.error(r.error || 'Erro ao retomar.')
            }
        } finally {
            setBusy(false)
        }
    }

    if (loading || !status?.subscription) return null

    const recurring = status.recurring

    return (
        <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
            <h2 className="text-lg font-semibold text-base-content mb-1 flex items-center gap-2">
                <MdAutorenew className="text-xl" />
                Pagamento automático
            </h2>
            <p className="text-sm text-base-content/60 mb-4">
                Deixe a mensalidade ser cobrada automaticamente no cartão a cada ciclo.
            </p>

            {/* Recorrência ativa */}
            {recurring && recurring.status === 'ACTIVE' && (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-success/10">
                        <MdCheckCircle className="text-2xl text-success" />
                        <div className="flex-1">
                            <p className="font-medium text-base-content">
                                Ativo — {recurring.creditCardBrand || 'Cartão'} ••••{' '}
                                {recurring.creditCardNumber || '????'}
                            </p>
                            <p className="text-xs text-base-content/60">
                                Próxima cobrança: {formatDate(recurring.nextChargeDate)}
                                {status.subscription.price
                                    ? ` — ${formatCurrency(status.subscription.price)}`
                                    : ''}
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost text-error w-fit"
                        onClick={() => setShowCancel(true)}
                        disabled={busy}
                    >
                        Cancelar pagamento automático
                    </button>
                </div>
            )}

            {/* Recorrência pausada (falha de cobrança) */}
            {recurring && recurring.status === 'PAUSED' && (
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/10">
                        <MdWarning className="text-2xl text-warning" />
                        <div className="flex-1">
                            <p className="font-medium text-base-content">
                                Pausado — houve falha na última cobrança
                            </p>
                            <p className="text-xs text-base-content/60">
                                Retome para tentar novamente no cartão{' '}
                                {recurring.creditCardBrand || ''} ••••{' '}
                                {recurring.creditCardNumber || '????'}.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            className="btn btn-sm btn-secondary w-fit"
                            onClick={handleResume}
                            disabled={busy}
                        >
                            Retomar
                        </button>
                        <button
                            className="btn btn-sm btn-ghost text-error w-fit"
                            onClick={() => setShowCancel(true)}
                            disabled={busy}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Sem recorrência: ativar */}
            {!recurring && !showForm && (
                <button className="btn btn-secondary gap-2" onClick={openForm}>
                    <MdCreditCard className="text-lg" />
                    Ativar pagamento automático
                </button>
            )}

            {!recurring && showForm && (
                <div className="flex flex-col gap-4">
                    <div className="alert alert-info text-sm">
                        {status.currentPeriodPaid
                            ? 'A mensalidade deste ciclo já está paga. Ao ativar, guardamos o cartão e a primeira cobrança automática sai no próximo vencimento.'
                            : 'Ao ativar, cobramos a mensalidade neste cartão agora e os próximos ciclos automaticamente.'}
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Nome no cartão</span>
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={cardHolder}
                            onChange={e => setCardHolder(e.target.value)}
                            placeholder="Nome como está no cartão"
                        />
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Número do cartão</span>
                        </label>
                        <input
                            className="input input-bordered w-full"
                            value={cardNumber}
                            onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                            placeholder="0000 0000 0000 0000"
                            maxLength={19}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Mês</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={expiryMonth}
                                onChange={e =>
                                    setExpiryMonth(onlyDigits(e.target.value).slice(0, 2))
                                }
                                placeholder="MM"
                                maxLength={2}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Ano</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={expiryYear}
                                onChange={e =>
                                    setExpiryYear(onlyDigits(e.target.value).slice(0, 4))
                                }
                                placeholder="AAAA"
                                maxLength={4}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">CVV</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={ccv}
                                onChange={e => setCcv(onlyDigits(e.target.value).slice(0, 4))}
                                placeholder="000"
                                maxLength={4}
                            />
                        </div>
                    </div>

                    <div className="divider text-xs text-base-content/40">
                        Dados do titular
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">CPF/CNPJ</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={holderCpfCnpj}
                                onChange={e => setHolderCpfCnpj(formatCpfCnpj(e.target.value))}
                                placeholder="000.000.000-00"
                                maxLength={18}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Email</span>
                            </label>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                value={holderEmail}
                                onChange={e => setHolderEmail(e.target.value)}
                                placeholder="email@exemplo.com"
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">CEP</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={holderPostalCode}
                                onChange={e =>
                                    setHolderPostalCode(formatPostalCode(e.target.value))
                                }
                                placeholder="00000-000"
                                maxLength={9}
                            />
                        </div>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Número</span>
                            </label>
                            <input
                                className="input input-bordered w-full"
                                value={holderAddressNumber}
                                onChange={e => setHolderAddressNumber(e.target.value)}
                                placeholder="123"
                            />
                        </div>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-secondary mt-0.5"
                            checked={consent}
                            onChange={e => setConsent(e.target.checked)}
                        />
                        <span className="text-sm text-base-content/70">
                            Autorizo a cobrança recorrente da assinatura neste cartão a
                            cada ciclo, até que eu cancele.
                        </span>
                    </label>

                    <div className="flex gap-2">
                        <button
                            className="btn btn-ghost flex-1"
                            onClick={() => setShowForm(false)}
                            disabled={submitting}
                        >
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary flex-1"
                            onClick={handleActivate}
                            disabled={submitting || !consent}
                        >
                            {submitting ? (
                                <span className="loading loading-spinner loading-sm"></span>
                            ) : status.currentPeriodPaid ? (
                                'Ativar cobrança automática'
                            ) : (
                                'Ativar e pagar'
                            )}
                        </button>
                    </div>
                </div>
            )}

            <ConfirmModal
                isOpen={showCancel}
                onClose={() => setShowCancel(false)}
                onConfirm={handleCancel}
                loading={busy}
                title="Cancelar pagamento automático"
                message="A cobrança automática no cartão será desativada. As próximas mensalidades voltarão a ser cobradas manualmente. Deseja continuar?"
                confirmButtonText="Cancelar recorrência"
            />
        </div>
    )
}
